import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpha } from '@mui/material/styles';
import { Box, FormControl, MenuItem, Select, TableCell, TableRow } from '@mui/material';
import type { PaginationState, SortingState, VisibilityState } from '@tanstack/react-table';
import TanStackDataTable from '../../table/TanStackDataTable';
import TableCsvExportButton from '../../table/TableCsvExportButton';
import { utilizationPrimaryHeaderCellSx } from '../../table/DataTable';
import { CheckboxColumnFilterMenu, NumericRangeColumnFilterMenu } from '../../dri-insights/ColumnFilterMenu';
import type { IndividualDetailsRow, DRIValueType } from '../../../types/driInsights';
import { SEARCE_COLORS } from '../../../theme';
import { TABLE_STYLES } from '../../../constants/tableStyles';
import { compareIndividualDetailsRows } from '../../../utils/driInsightsUtils';
import { buildIndividualDetailsDataTableColumns } from './individualDetailsDataTableColumns';
import { exportIndividualDetailsGridToCsv } from './individualDetailsTableCsvExport';
import { DRI_VALUE_TYPE_SELECT_OPTIONS } from '../../dri-insights/DriEmployeeWiseSectionToolbar';

/** Filter state for Individual Details table. Parent owns state. */
export interface IndividualDetailsFilterState {
  filterAnchorEl: Record<string, HTMLElement | null>;
  columnFilters: Record<string, { selectedValues?: string[]; min?: number | null; max?: number | null }>;
  tempSelectedValues: Record<string, string[] | undefined>;
  filterSearchText: Record<string, string>;
  tempNumericFilters?: Record<string, { min?: number | null; max?: number | null }>;
}

/** Filter handlers for Individual Details table. */
export interface IndividualDetailsFilterHandlers {
  getAvailableValuesForColumn: (columnId: string) => string[];
  hasActiveFilter: (columnId: string) => boolean;
  onFilterClose: (columnId: string) => void;
  onApplyCheckboxFilter: (columnId: string) => void;
  onSelectAllCheckboxFilter: (columnId: string, values: string[]) => void;
  onClearAllCheckboxFilter: (columnId: string) => void;
  onCheckboxFilterToggle: (columnId: string, value: string) => void;
  onFilterSearchTextChange: (columnId: string, value: string) => void;
  onClearFilterForColumn: (columnId: string) => void;
  getNumericRange?: (columnId: string) => { min: number; max: number };
  onApplyNumericFilter?: (columnId: string) => void;
  onNumericFilterChange?: (columnId: string, type: 'min' | 'max', value: string) => void;
}

interface IndividualDetailsTableProps {
  rows: IndividualDetailsRow[];
  gridColumns: string[];
  gridCellValueType: DRIValueType;
  onGridCellValueTypeChange: (value: DRIValueType) => void;
  /** When false, hide the value-type dropdown (rare; parent may control elsewhere). Default true. */
  showDriValueTypeSelect?: boolean;
  onFilterClick: (
    event: React.MouseEvent<HTMLElement>,
    columnId: string,
    useCheckbox?: boolean,
    isNumeric?: boolean,
    isDateRange?: boolean
  ) => void;
  filterState: IndividualDetailsFilterState;
  filterHandlers: IndividualDetailsFilterHandlers;
  flushChrome?: boolean;
  paginationResetDeps?: unknown;
}

const IndividualDetailsTable: React.FC<IndividualDetailsTableProps> = ({
  rows,
  gridColumns,
  gridCellValueType,
  onGridCellValueTypeChange,
  showDriValueTypeSelect = true,
  onFilterClick,
  filterState,
  filterHandlers,
  flushChrome = false,
  paginationResetDeps,
}) => {
  const {
    filterAnchorEl,
    columnFilters,
    tempSelectedValues,
    filterSearchText,
    tempNumericFilters = {},

  } = filterState;
  const {
    getAvailableValuesForColumn,
    hasActiveFilter,
    onFilterClose,
    onApplyCheckboxFilter,
    onSelectAllCheckboxFilter,
    onClearAllCheckboxFilter,
    onCheckboxFilterToggle,
    onFilterSearchTextChange,
    onClearFilterForColumn,
    getNumericRange,
    onApplyNumericFilter,
    onNumericFilterChange,
  } = filterHandlers;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [paginationResetDeps]);

  useEffect(() => {
    setSorting((prev) => {
      if (prev.length === 0) return prev;
      const id = prev[0].id;
      const allowed = new Set(['individual_email', ...gridColumns.map((c) => `product_${c}`)]);
      return allowed.has(id) ? prev : [];
    });
  }, [gridColumns]);

  const handleSort = useCallback((columnId: string) => {
    setSorting((prev) => {
      const cur = prev[0];
      if (cur?.id === columnId) return [{ id: columnId, desc: !cur.desc }];
      return [{ id: columnId, desc: false }];
    });
  }, []);

  const compareRows = useCallback(
    (a: IndividualDetailsRow, b: IndividualDetailsRow, columnId: string) =>
      compareIndividualDetailsRows(a, b, columnId, gridCellValueType),
    [gridCellValueType]
  );

  const sortableColumnIds = useMemo(
    () => ['individual_email','drp_id', ...gridColumns.map((c) => `product_${c}`)],
    [gridColumns]
  );

  const dataTableColumns = useMemo(
    () =>
      buildIndividualDetailsDataTableColumns({
        gridColumns,
        gridCellValueType,
        onSort: handleSort,
        onFilterClick,
        hasActiveFilter,
      }),
    [gridColumns, gridCellValueType, handleSort, onFilterClick, hasActiveFilter]
  );

  const renderCheckboxFilterMenu = (columnId: string, columnLabel: string) => {
    const anchorEl = filterAnchorEl[columnId];
    const open = Boolean(anchorEl);
    const filter = columnFilters[columnId] || {};
    const currentApplied = filter.selectedValues || [];
    const currentTemp = tempSelectedValues[columnId];
    const selectedValues = currentTemp !== undefined ? currentTemp : currentApplied;
    const searchText = filterSearchText[columnId] || '';
    const availableValues = getAvailableValuesForColumn(columnId);
    const filteredOptions = availableValues.filter((v) => v.toLowerCase().includes(searchText.toLowerCase()));
    const hasFilter = !!(filter.selectedValues && filter.selectedValues.length > 0);

    return (
      <CheckboxColumnFilterMenu
        open={open}
        anchorEl={anchorEl}
        onClose={() => onFilterClose(columnId)}
        columnLabel={columnLabel}
        options={filteredOptions}
        selectedValues={selectedValues}
        onToggle={(value) => onCheckboxFilterToggle(columnId, value)}
        searchText={searchText}
        onSearchTextChange={(v) => onFilterSearchTextChange(columnId, v)}
        onSelectAll={() => onSelectAllCheckboxFilter(columnId, filteredOptions)}
        onClearAll={() => onClearAllCheckboxFilter(columnId)}
        onApply={() => onApplyCheckboxFilter(columnId)}
        hasFilter={hasFilter}
        onClearFilter={() => {
          onClearFilterForColumn(columnId);
          onFilterClose(columnId);
        }}
      />
    );
  };

  const renderNumericRangeFilterMenu = (columnId: string, columnLabel: string) => {
    if (!getNumericRange || !onApplyNumericFilter || !onNumericFilterChange) return null;
    const anchorEl = filterAnchorEl[columnId];
    const open = Boolean(anchorEl);
    const filter = columnFilters[columnId] || {};
    const tempFilter = tempNumericFilters[columnId];
    const currentApplied = filter;
    const min =
      tempFilter?.min !== undefined
        ? tempFilter.min
        : currentApplied.min !== null && currentApplied.min !== undefined
          ? currentApplied.min
          : null;
    const max =
      tempFilter?.max !== undefined
        ? tempFilter.max
        : currentApplied.max !== null && currentApplied.max !== undefined
          ? currentApplied.max
          : null;
    const range = getNumericRange(columnId);
    const hasFilter =
      (currentApplied.min != null && currentApplied.min !== undefined) ||
      (currentApplied.max != null && currentApplied.max !== undefined);

    return (
      <NumericRangeColumnFilterMenu
        open={open}
        anchorEl={anchorEl}
        onClose={() => onFilterClose(columnId)}
        columnLabel={columnLabel}
        rangeHint={`Range: ${range.min.toFixed(2)} - ${range.max.toFixed(2)}`}
        min={min ?? null}
        max={max ?? null}
        onMinChange={(v) => onNumericFilterChange!(columnId, 'min', v === null ? '' : String(v))}
        onMaxChange={(v) => onNumericFilterChange!(columnId, 'max', v === null ? '' : String(v))}
        onApply={() => onApplyNumericFilter(columnId)}
        onClear={() => {
          onClearFilterForColumn(columnId);
          onFilterClose(columnId);
        }}
        hasFilter={!!hasFilter}
      />
    );
  };

  const columnMenuLabels = useMemo(() => {
    const labels: Record<string, string> = { individual_email: 'Email' };
    labels['drp_id'] = 'DRP ID';
    gridColumns.forEach((product) => {
      labels[`product_${product}`] = product;
    });
    return labels;
  }, [gridColumns, rows]);

  const csvColumnIdsInOrder = useMemo(
    () => ['individual_email','drp_id', ...gridColumns.map((c) => `product_${c}`)] as const,
    [gridColumns]
  );

  const valueTypeSelectHeader = useMemo(
    () =>
      showDriValueTypeSelect ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
          <FormControl size="small" sx={{ minWidth: 200, maxWidth: 280, flexShrink: 0 }}>
            <Select
              value={gridCellValueType}
              onChange={(e) => onGridCellValueTypeChange(e.target.value as DRIValueType)}
              sx={{
                fontSize: '0.75rem',
                height: '36px',
                backgroundColor: SEARCE_COLORS.white,
                '& .MuiSelect-select': { py: 0.75 },
              }}
            >
              {DRI_VALUE_TYPE_SELECT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TableCsvExportButton
            disabled={rows.length === 0}
            onClick={() =>
              exportIndividualDetailsGridToCsv({
                rows,
                columnVisibility,
                columnIdsInOrder: csvColumnIdsInOrder,
                columnLabels: columnMenuLabels,
                gridCellValueType,
                filenameBase: 'dri-individual-details',
              })
            }
          />
        </Box>
      ) : (
        <TableCsvExportButton
          disabled={rows.length === 0}
          onClick={() =>
            exportIndividualDetailsGridToCsv({
              rows,
              columnVisibility,
              columnIdsInOrder: csvColumnIdsInOrder,
              columnLabels: columnMenuLabels,
              gridCellValueType,
              filenameBase: 'dri-individual-details',
            })
          }
        />
      ),
    [showDriValueTypeSelect, gridCellValueType, onGridCellValueTypeChange, rows, columnVisibility, csvColumnIdsInOrder, columnMenuLabels]
  );

  const paperSx = {
    p: 0,
    mb: flushChrome ? 0 : 1,
    boxShadow: 'none',
    border: flushChrome ? 'none' : `1px solid ${SEARCE_COLORS.neutral_200}`,
    borderRadius: flushChrome ? 0 : 2,
    backgroundColor: flushChrome ? 'transparent' : SEARCE_COLORS.white,
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0,
    width: '100%',
    maxWidth: '100%',
  };

  const tableContainerSx = {
    border: 'none',
    borderRadius: flushChrome ? 0 : 2,
    outline: 'none',
    flex: '1 1 auto',
    overflowX: 'auto',
  };

  const paginationSx = {
    borderTop: `1px solid ${alpha(SEARCE_COLORS.border, 0.9)}`,
    mb: flushChrome ? 0 : 4,
    flexShrink: 0,
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
      color: SEARCE_COLORS.textSecondary,
      fontWeight: 500,
    },
    '& .MuiTablePagination-select': {
      color: SEARCE_COLORS.primary,
      fontWeight: 600,
    },
    '& .MuiIconButton-root': {
      color: SEARCE_COLORS.primary,
      '&:hover': {
        backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
      },
    },
  };

  return (
    <>
      <TanStackDataTable<IndividualDetailsRow>
        tableId="dri-individual-details"
        data={rows}
        dataTableColumns={dataTableColumns}
        getRowId={(row) => row.employee.member_email}
        sorting={sorting}
        onSortingChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        sortableColumnIds={sortableColumnIds}
        compareRows={compareRows}
        headerAction={valueTypeSelectHeader}
        loading={false}
        empty={false}
        maxHeight={450}
        rowsPerPageOptions={[10, 25, 50, 100]}
        elevation={0}
        headerCellBaseSx={utilizationPrimaryHeaderCellSx}
        embedded={flushChrome}
        enableColumnVisibility
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnMenuLabels={columnMenuLabels}
        rowHover
        tableSx={{
          width: 'max-content',
          minWidth: '100%',
          tableLayout: 'auto' as const,
          '& .MuiTableCell-root': { borderRight: 'none', borderLeft: 'none' },
          ...(flushChrome ? { '& thead .MuiTableCell-root': { borderRadius: 0 } } : {}),
        }}
        paperSx={paperSx}
        tableContainerSx={tableContainerSx}
        paginationSx={paginationSx}
        renderEmptyBody={(visibleColumnCount) => (
          <TableRow>
            <TableCell
              colSpan={visibleColumnCount}
              sx={{
                ...TABLE_STYLES.cellEmpty,
                fontSize: '0.75rem',
                textAlign: 'center',
                color: SEARCE_COLORS.textSecondary,
                py: 3,
              }}
            >
              No data available
            </TableCell>
          </TableRow>
        )}
      />
      {renderCheckboxFilterMenu('individual_email', 'Email')}
      {renderCheckboxFilterMenu('drp_id', 'DRP ID')}
      {gridColumns.map((product) => renderNumericRangeFilterMenu(`product_${product}`, product))}
    </>
  );
};

export default IndividualDetailsTable;
